import React, { useState, useEffect } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';

const QuizModeSelector = () => {
    const { t } = useTranslation();
    const { currentQCM, sessionContext, currentQuestionType, changeQuestionType } = useQCM();
    const [selectedMode, setSelectedMode] = useState('qcm'); // par défaut

    // Quiz modes with updated labels, icons, and detailed tooltips
    const modes = [
        {
            id: 'qcm',
            name: 'Exam style',
            label: 'Exam style',
            tooltip: 'QCM as in the Exam to get accustomed. Evaluation is also about attention to complex situations and details not only technical aspects',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                </svg>
            ),
            questionType: 'qcm'
        },
        {
            id: 'funnel',
            name: 'Funnel',
            label: 'Funnel',
            tooltip: 'Provide the learning path to find the best answer and group similar concepts or choices to highlight the subtle differences.',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
            ),
            questionType: 'funnel-qcm'
        },
        {
            id: 'booster',
            name: 'Booster',
            label: 'Booster',
            tooltip: 'Focuses attention exclusively on key elements for decision-making by reducing the amount of information presented to the learner. It\'s very useful when the foundations are solid and you need to cement what you\'ve learned.',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            questionType: 'booster-qcm',
            disabled: true
        }
    ];

    // Détecter le mode actuel basé sur currentQuestionType du contexte
    useEffect(() => {
        if (currentQuestionType) {
            const detectedMode = modes.find(mode => mode.questionType === currentQuestionType);
            if (detectedMode) {
                setSelectedMode(detectedMode.id);
            }
        }
    }, [currentQuestionType]);

    // Gestionnaire de changement de mode
    const handleModeChange = async (modeId) => {
        const mode = modes.find(m => m.id === modeId);
        
        if (mode && !mode.disabled && modeId !== selectedMode) {
            setSelectedMode(modeId);
            
            // Changer le type de question via le contexte
            try {
                await changeQuestionType(mode.questionType);
            } catch (error) {
                console.error('Error changing quiz mode:', error);
                // Rétablir le mode précédent en cas d'erreur
                const currentMode = modes.find(m => m.questionType === currentQuestionType);
                if (currentMode) {
                    setSelectedMode(currentMode.id);
                }
            }
        }
    };

    return (
        <div className="flex items-center space-x-3">
            {modes.map((mode) => {
                const isSelected = selectedMode === mode.id;
                const isDisabled = mode.disabled;
                
                return (
                    <div key={mode.id} className="relative group">
                        <button
                            onClick={() => !isDisabled && handleModeChange(mode.id)}
                            disabled={isDisabled}
                            className={`
                                relative flex items-center space-x-3 px-5 py-3 rounded-2xl font-semibold text-sm
                                transition-all duration-300 ease-in-out transform border-2
                                ${isSelected 
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl shadow-blue-500/30 scale-105 border-transparent' 
                                    : isDisabled
                                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed opacity-50 border-gray-700'
                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white hover:shadow-lg hover:scale-102 border-gray-600 hover:border-gray-500'
                                }
                            `}
                        >
                            {/* Icon */}
                            <span className={`
                                transition-colors duration-200
                                ${isSelected ? 'text-white' : isDisabled ? 'text-gray-500' : 'text-gray-400 group-hover:text-white'}
                            `}>
                                {mode.icon}
                            </span>
                            
                            {/* Label */}
                            <span className="whitespace-nowrap">
                                {mode.label}
                            </span>
                            
                            {/* Active indicator */}
                            {isSelected && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg"></div>
                            )}
                            
                            {/* "Soon" badge for disabled modes */}
                            {mode.disabled && (
                                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                                    Soon
                                </span>
                            )}
                        </button>
                        
                        {/* Enhanced tooltip - positioned below button */}
                        <div className={`
                            absolute top-full left-1/2 transform -translate-x-1/2 mt-3 px-4 py-3
                            bg-gray-800/95 backdrop-blur-sm text-white text-sm rounded-xl shadow-2xl 
                            border border-gray-600/50 opacity-0 group-hover:opacity-100 
                            transition-all duration-300 ease-out pointer-events-none
                            whitespace-normal z-50 max-w-lg text-center leading-relaxed
                            group-hover:translate-y-1 group-hover:scale-105
                        `}>
                            <div className="font-medium text-gray-100 mb-1">
                                {mode.label}
                            </div>
                            <div className="text-gray-300 text-xs leading-snug">
                                {mode.tooltip}
                            </div>
                            {/* Tooltip arrow */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 
                                         border-l-6 border-r-6 border-b-6 border-transparent 
                                         border-b-gray-800/95 filter drop-shadow-sm"></div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default QuizModeSelector;
